import type { CitationOutput } from "@/components/tool-kit/source-citation-builder";
import {
  type CitationParts,
  mlaDate,
  type PartialDate,
  parsePartialDate,
  tidyParts,
} from "@/lib/tool-kit/citation-parts";
import {
  capitalizeSubtitle,
  closer,
  fullDate,
  get,
  italic,
  type LegalValues,
  mainTitle,
  mlaUrl,
  plain,
  titleCase,
} from "@/lib/tool-kit/legal-sources/common";

/*
 * United Nations resolutions and documents in APA 7, MLA 9 and Chicago 18.
 *
 * Sources checked (October 2026):
 * - Chicago 18 §14.207 via the CMOS Q&A on the Universal Declaration of Human Rights
 *   (answer relying on CMOS 18): "UN General Assembly, Resolution 217 (III) A,
 *   Universal Declaration of Human Rights, A/RES/217(III) (December 10, 1948),
 *   https://www.un.org/…" — body, resolution, title, document symbol, date. UN documents
 *   are cited in notes only (Columbia College CMOS 18 guide).
 * - MLA 9: MLA Style Center, "Documenting Legal Works" — "United Nations, General
 *   Assembly. Universal Declaration of Human Rights. Resolution 217 A, 10 Dec. 1948.
 *   United Nations, www.un.org/…" — the organ as author, the title of a resolution in
 *   roman (a political document), the resolution number and date, then the website.
 *   A report is a self-contained work, so its title is italic.
 * - APA 7 has no UN-resolution template. Its report pattern for group authors (§10.4)
 *   fits: author, year, italic title, the document symbol in parentheses where a report
 *   number goes, and the parent organization as publisher — as in APA's own example of
 *   an institute's report published by its parent department.
 * - Symbols and dates for the page examples: UN Dag Hammarskjöld Library resolution
 *   tables and the documents themselves — A/RES/70/1, adopted 25 September 2015; S/RES/
 *   2334 (2016), adopted 23 December 2016. Security Council resolutions carry their year
 *   in the symbol.
 */

export const UN_DOCUMENT_FIELDS = {
  body: "body",
  kind: "kind",
  number: "number",
  title: "title",
  shortTitle: "shortTitle",
  symbol: "symbol",
  date: "date",
  website: "website",
  url: "url",
} as const;

const F = UN_DOCUMENT_FIELDS;

export type UnBody = "ga" | "sc" | "ecosoc" | "hrc" | "sg";
export type UnDocumentKind = "resolution" | "document";

/** The body as each style names it. */
const BODIES: Readonly<
  Record<UnBody, { apa: string; mla: string; chicago: string }>
> = {
  ga: {
    apa: "United Nations General Assembly",
    mla: "United Nations, General Assembly",
    chicago: "UN General Assembly",
  },
  sc: {
    apa: "United Nations Security Council",
    mla: "United Nations, Security Council",
    chicago: "UN Security Council",
  },
  ecosoc: {
    apa: "United Nations Economic and Social Council",
    mla: "United Nations, Economic and Social Council",
    chicago: "UN Economic and Social Council",
  },
  hrc: {
    apa: "United Nations Human Rights Council",
    mla: "United Nations, Human Rights Council",
    chicago: "UN Human Rights Council",
  },
  sg: {
    apa: "United Nations Secretary-General",
    mla: "United Nations, Secretary-General",
    chicago: "UN Secretary-General",
  },
};

export interface UnDocumentLabels {
  apa: string;
  mla: string;
  chicago: string;
  referenceList: string;
  parenthetical: string;
  narrative: string;
  worksCited: string;
  inText: string;
  footnote: string;
  apaNote: string;
  mlaNote: string;
  chicagoNote: string;
}

interface UnDocument {
  body: (typeof BODIES)[UnBody];
  isSecurityCouncil: boolean;
  isResolution: boolean;
  number: string;
  title: string;
  shortTitle: string;
  symbol: string;
  date: PartialDate | null;
  year: string;
  website: string;
  url: string;
}

const RE_RESOLUTION_PREFIX = /^(resolution|res\.?)\s*/i;

function readDocument(values: LegalValues): UnDocument {
  const bodyKey = (get(values, F.body) || "ga") as UnBody;
  const date = parsePartialDate(get(values, F.date));
  return {
    body: BODIES[bodyKey] ?? BODIES.ga,
    isSecurityCouncil: bodyKey === "sc",
    isResolution: (get(values, F.kind) || "resolution") === "resolution",
    number: get(values, F.number).replace(RE_RESOLUTION_PREFIX, ""),
    title: get(values, F.title),
    shortTitle: get(values, F.shortTitle),
    symbol: get(values, F.symbol),
    date,
    year: date ? String(date.year) : "n.d.",
    website: get(values, F.website),
    url: get(values, F.url),
  };
}

/** "Resolution 70/1"; a Security Council resolution carries its year: "Resolution 2334 (2016)". */
function designation(doc: UnDocument, withYear: boolean): string {
  if (!doc.number) {
    return "";
  }
  const year =
    withYear && doc.isSecurityCouncil && doc.date ? ` (${doc.year})` : "";
  return `Resolution ${doc.number}${year}`;
}

function apaOutput(doc: UnDocument, labels: UnDocumentLabels): CitationOutput {
  const generated = doc.title === "" && doc.isResolution;
  const title = generated
    ? designation(doc, true)
    : capitalizeSubtitle(doc.title);
  const symbol = !generated && doc.symbol ? ` (${doc.symbol})` : "";
  const parts: CitationParts = [
    plain(`${doc.body.apa}. (${doc.year}). `),
    italic(title),
    plain(`${symbol}. United Nations.${doc.url ? ` ${doc.url}` : ""}`),
  ];
  return {
    key: "apa",
    label: labels.apa,
    entries: [
      { label: labels.referenceList, parts: tidyParts(parts) },
      {
        label: labels.parenthetical,
        parts: [plain(`(${doc.body.apa}, ${doc.year})`)],
      },
      {
        label: labels.narrative,
        parts: [plain(`${doc.body.apa} (${doc.year})`)],
      },
    ],
    note: labels.apaNote,
  };
}

/** The title-or-description element and the number element of an MLA entry. */
function mlaSourceParts(doc: UnDocument): CitationParts {
  const date = mlaDate(doc.date);
  if (!doc.isResolution) {
    const title = titleCase(doc.title);
    const number = [doc.symbol, date].filter(Boolean).join(", ");
    return [
      italic(title),
      plain(`${closer(title)} ${number ? `${number}. ` : ""}`),
    ];
  }
  const number = [designation(doc, true), date].filter(Boolean).join(", ");
  if (doc.title === "") {
    return [plain(`${number}. `)];
  }
  const title = titleCase(doc.title);
  return [plain(`${title}${closer(title)} ${number ? `${number}. ` : ""}`)];
}

function mlaInText(doc: UnDocument): CitationParts {
  if (doc.isResolution && doc.title === "") {
    return [plain(`(United Nations, ${designation(doc, false)})`)];
  }
  const short = titleCase(doc.shortTitle || mainTitle(doc.title));
  return doc.isResolution
    ? [plain(`(United Nations, ${short})`)]
    : [plain("(United Nations, "), italic(short), plain(")")];
}

function mlaOutput(doc: UnDocument, labels: UnDocumentLabels): CitationOutput {
  const parts: CitationParts = [
    plain(`${doc.body.mla}. `),
    ...mlaSourceParts(doc),
  ];
  const location = mlaUrl(doc.url);
  if (doc.website) {
    parts.push(italic(doc.website));
    parts.push(plain(location ? `, ${location}.` : "."));
  } else if (location) {
    parts.push(plain(`${location}.`));
  }
  return {
    key: "mla",
    label: labels.mla,
    entries: [
      { label: labels.worksCited, parts: tidyParts(parts) },
      { label: labels.inText, parts: mlaInText(doc) },
    ],
    note: labels.mlaNote,
  };
}

function chicagoOutput(
  doc: UnDocument,
  labels: UnDocumentLabels
): CitationOutput {
  const date = fullDate(doc.date);
  const tail = `${doc.symbol ? `, ${doc.symbol}` : ""}${date ? ` (${date})` : ""}${
    doc.url ? `, ${doc.url}` : ""
  }.`;
  let parts: CitationParts;
  if (doc.isResolution) {
    const head = [
      doc.body.chicago,
      designation(doc, false),
      titleCase(doc.title),
    ]
      .filter(Boolean)
      .join(", ");
    parts = [plain(`${head}${tail}`)];
  } else {
    parts = [
      plain(`${doc.body.chicago}, `),
      italic(titleCase(doc.title)),
      plain(tail),
    ];
  }
  return {
    key: "chicago",
    label: labels.chicago,
    entries: [{ label: labels.footnote, parts: tidyParts(parts) }],
    note: labels.chicagoNote,
  };
}

export function unDocumentIsReady(values: LegalValues): boolean {
  const resolution = (get(values, F.kind) || "resolution") === "resolution";
  const identified = resolution
    ? get(values, F.number) !== "" || get(values, F.symbol) !== ""
    : get(values, F.title) !== "" && get(values, F.symbol) !== "";
  return identified && parsePartialDate(get(values, F.date)) !== null;
}

export function formatUnDocument(
  values: LegalValues,
  labels: UnDocumentLabels
): CitationOutput[] {
  const doc = readDocument(values);
  return [
    apaOutput(doc, labels),
    mlaOutput(doc, labels),
    chicagoOutput(doc, labels),
  ];
}
